// ===================================================
// useAuth.js — 로그인/로그아웃 상태 관리 훅
//
// 역할:
//   - 앱 시작 시 현재 로그인 상태를 Supabase에서 확인
//   - 로그인·로그아웃·비밀번호 재설정 이벤트를 실시간 감지
//   - Google OAuth 로그인 시 provider_token 캡처 후 콜백 호출
//   - user: null(확인 중) / 객체(로그인됨) / false(비로그인)
//   - passwordRecovery: 비밀번호 재설정 이메일 링크로 돌아왔을 때 true
//
// 네이티브 앱 OAuth 처리:
//   - @capacitor/browser로 구글 로그인 완료 후
//   - com.omsal.myapp://auth/callback?code=xxx 형태로 앱에 돌아옴
//   - App.addListener('appUrlOpen')으로 코드를 받아 세션으로 교환
// ===================================================

import { useState, useEffect, useRef } from 'react';
import { Capacitor } from '@capacitor/core';
import { App as CapacitorApp } from '@capacitor/app';
import { Browser } from '@capacitor/browser';
import { supabase } from '../supabase';
import { checkIsMaster } from '../storage';
import { logError } from '../utils/errorLogger';

export function useAuth(onGoogleTokens) {
  // null: 아직 확인 중, 객체: 로그인된 사용자, false: 비로그인
  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  // 비밀번호 재설정 이메일 링크를 클릭해 돌아온 경우 true
  const [passwordRecovery, setPasswordRecovery] = useState(false);
  // 마스터 어드민 계정 여부
  const [isMaster, setIsMaster] = useState(false);

  // 콜백 ref (리렌더링 시에도 최신 콜백 참조)
  const onGoogleTokensRef = useRef(onGoogleTokens);
  useEffect(() => { onGoogleTokensRef.current = onGoogleTokens; }, [onGoogleTokens]);

  // 이전에 로그인 상태였는지 추적 — 예상치 못한 로그아웃 감지용
  const wasLoggedInRef = useRef(false);

  useEffect(() => {
    // 이메일 인증이 완료된 사용자만 로그인 상태로 처리
    // email_confirmed_at: Supabase가 이메일 인증 완료 시점을 기록하는 필드
    // Google OAuth 유저는 email_confirmed_at이 자동으로 설정됨
    const resolveUser = (session) => {
      const u = session?.user;
      return (u && u.email_confirmed_at) ? u : false;
    };

    // Google OAuth provider_token이 있으면 캡처
    const captureGoogleToken = (session) => {
      if (session?.provider_token && onGoogleTokensRef.current) {
        onGoogleTokensRef.current(
          session.provider_token,
          session.provider_refresh_token || null,
        );
      }
    };

    // PKCE 인증 코드가 URL에 남아있으면 제거
    // 이메일 인증/비밀번호 재설정 후 ?code=xxx가 URL에 남은 상태에서
    // 새로고침하면 Supabase가 이미 사용된 코드를 재교환 시도 → 실패 → 세션 초기화 발생
    const cleanAuthParamsFromUrl = () => {
      const url = new URL(window.location.href);
      const hasAuthParams = url.searchParams.has('code') ||
        url.searchParams.has('error') ||
        url.searchParams.has('error_description');
      if (hasAuthParams) {
        url.searchParams.delete('code');
        url.searchParams.delete('error');
        url.searchParams.delete('error_description');
        window.history.replaceState({}, '', url.pathname + url.search + url.hash);
      }
      // 해시 기반 파라미터도 제거 (#access_token=... 등)
      if (window.location.hash && window.location.hash.includes('access_token')) {
        window.history.replaceState({}, '', url.pathname + url.search);
      }
    };

    // ── 1단계: getSession()으로 즉시 세션 확인 (localStorage 읽기) ──
    // 8초 타임아웃: 네트워크 문제 시 흰 화면 방지
    Promise.race([
      supabase.auth.getSession(),
      new Promise((_, reject) => setTimeout(() => reject(new Error('timeout')), 8000)),
    ])
      .then(({ data: { session } }) => {
        const resolvedUser = resolveUser(session);
        setUser(resolvedUser);
        captureGoogleToken(session);
        setAuthLoading(false);
        cleanAuthParamsFromUrl();
        // 마스터 여부는 비동기로 확인 (로딩 차단하지 않음)
        if (resolvedUser && resolvedUser.id) {
          checkIsMaster(resolvedUser.id).then(setIsMaster).catch(() => {});
        }
      })
      .catch(() => {
        setUser(false);
        setAuthLoading(false);
      });

    // ── 2단계: onAuthStateChange로 이후 이벤트 감지 ──
    // INITIAL_SESSION: getSession()과 중복이지만 무해 (user를 다시 설정할 뿐)
    // TOKEN_REFRESHED: 자동 갱신 완료 시 최신 세션으로 업데이트
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (event === 'INITIAL_SESSION') {
        // getSession()이 이미 처리했으므로 user만 조용히 갱신
        const resolvedUser = resolveUser(session);
        setUser(resolvedUser);
        if (resolvedUser && resolvedUser.id) {
          checkIsMaster(resolvedUser.id).then(setIsMaster).catch(() => {});
        }
        return;
      }

      if (event === 'TOKEN_REFRESHED') {
        // 토큰 자동 갱신 완료 — user 업데이트 (세션 최신화)
        setUser(resolveUser(session));
        return;
      }

      if (event === 'PASSWORD_RECOVERY') {
        setPasswordRecovery(true);
        setUser(resolveUser(session));
      } else {
        setPasswordRecovery(false);
        const resolvedUser = resolveUser(session);

        // 로그인 상태에서 갑자기 SIGNED_OUT이 오면 JWT 만료 or 네트워크 문제일 가능성
        // → 에러 로그 기록 (유저가 직접 로그아웃한 경우는 handleLogout에서 명시적으로 처리)
        if (event === 'SIGNED_OUT' && wasLoggedInRef.current) {
          logError({
            action: 'unexpectedSignedOut',
            error: new Error('Auth session lost — JWT expired or network refresh failed'),
            screen: 'auth',
          });
        }

        // 로그인 상태 추적 업데이트
        if (resolvedUser) wasLoggedInRef.current = true;
        else if (event === 'SIGNED_OUT') wasLoggedInRef.current = false;

        setUser(resolvedUser);
        // 로그인 이벤트 시 마스터 여부 재확인
        if (event === 'SIGNED_IN' && resolvedUser && resolvedUser.id) {
          const master = await checkIsMaster(resolvedUser.id);
          setIsMaster(master);
        } else if (event === 'SIGNED_OUT') {
          setIsMaster(false);
        }
      }

      // Google OAuth 로그인 시 provider_token 캡처
      if (event === 'SIGNED_IN') {
        captureGoogleToken(session);
      }

      // 인증 이벤트 처리 후 URL 정리
      if (event === 'SIGNED_IN' || event === 'PASSWORD_RECOVERY') {
        cleanAuthParamsFromUrl();
      }
    });

    // ── 네이티브 앱 OAuth 콜백 처리 ────────────────────────────
    // 구글 로그인 완료 후 com.omsal.myapp://auth/callback?code=xxx 형태로 돌아옴.
    // PKCE 방식: code를 받아 Supabase 세션으로 교환 → onAuthStateChange 자동 발동
    let appUrlListenerHandle = null;
    if (Capacitor.isNativePlatform()) {
      appUrlListenerHandle = CapacitorApp.addListener('appUrlOpen', async ({ url }) => {
        if (!url.startsWith('com.omsal.myapp://')) return;
        try {
          const urlObj = new URL(url);
          const code = urlObj.searchParams.get('code');
          if (code) {
            // 인증 코드를 실제 로그인 세션으로 교환
            await supabase.auth.exchangeCodeForSession(code);
          }
        } finally {
          // 인앱 브라우저 닫기
          await Browser.close();
        }
      });
    }

    // 컴포넌트가 사라질 때 이벤트 구독 해제 (메모리 누수 방지)
    return () => {
      subscription.unsubscribe();
      if (appUrlListenerHandle) {
        appUrlListenerHandle.then?.(l => l.remove()).catch(() => {});
      }
    };
  }, []);

  return { user, authLoading, passwordRecovery, setPasswordRecovery, isMaster };
}
